import { useCallback, useEffect, useState } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useClientTranslation } from '@/lib/core/i18n';
import { SheetHeader } from '@/components/shared/shop/ui/SheetHeader';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface EditListNameSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  onSave: (name: string) => Promise<void>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function EditListNameSheet({ open, onOpenChange, initialName, onSave }: EditListNameSheetProps) {
  const t = useClientTranslation();
  const [name, setName] = useState(initialName);
  const [isSubmitting, setSubmitting] = useState(false);

  // Reset to the current list name each time the sheet re-opens.
  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSave = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || isSubmitting) return;
    setSubmitting(true);
    try {
      await onSave(trimmed);
    } finally {
      setSubmitting(false);
    }
  }, [name, isSubmitting, onSave]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex flex-col">
        <SheetHeader title={t('App.Shop.Collection.EditListName')} />

        {/* List name input, pre-filled with the current name */}
        <div className="flex flex-col gap-1 px-4 pt-[9px] pb-10">
          <Label htmlFor="edit-list-name" className="font-inter">
            {t('App.Shop.Favorites.ListNameLabel')}
          </Label>
          <Input id="edit-list-name" value={name} onChange={e => setName(e.target.value)} />
        </div>

        {/* Save / Cancel actions */}
        <div className="border-jncquoi-neutral-400 flex shrink-0 flex-col gap-1 border-t-[0.5px] px-4 pt-6 pb-10.5">
          <Button
            variant="primary-solid"
            size="md"
            onClick={handleSave}
            disabled={!name.trim() || isSubmitting}
            className="w-full"
          >
            {t('App.Labels.SaveChanges')}
          </Button>
          <Button variant="tertiary-link" size="sm" onClick={handleCancel} className="w-full">
            {t('App.Labels.Cancel')}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
